import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import Wizard, { Step } from 'components/Wizard';
import ClipVideoStep, {
  ClipSelectionMode,
} from 'containers/ProjectWizards/ClipVideoStep';
import useVideoTranscriptDurationBalance from 'hooks/useVideoTranscriptDurationBalance';
import { entireVideoEnhancer } from 'redux/modules/video-upload';
import { VideoFlowAction, VideoFlowState } from '../state/types';
import { VideoFlowStep } from '../types';
import { block } from '../utils';
import ClipVideoStepContinueButton from './ClipVideoStepContinueButton';

export type UseClipVideoStepProps = {
  state: VideoFlowState;
  stateDispatch: React.Dispatch<VideoFlowAction>;
  wizardRef: React.RefObject<Wizard<VideoFlowStep>>;
};

export default function useClipVideoStep({
  state,
  stateDispatch,
  wizardRef,
}: UseClipVideoStepProps) {
  const isUploadingEntireVideo = useSelector(
    entireVideoEnhancer.selectors.isUploadingEntireVideoSelector,
  );
  const audioPeaks = useSelector(
    entireVideoEnhancer.selectors.audioPeaksSelector,
  );

  const {
    aspectRatioName,
    clipEndMillis,
    clipStartMillis,
    isFullEpisode,
    videoSrcUrl,
  } = state;

  const {
    canTranscribe,
    newBalanceMillis,
    outOfTimeMessage,
    showBalance,
  } = useVideoTranscriptDurationBalance({
    durationMillis: (clipEndMillis ?? clipStartMillis) - clipStartMillis,
    transcriptionEnabled: true,
  });

  const clipSelectionMode: ClipSelectionMode = isFullEpisode
    ? 'full-episode'
    : 'clip';

  const handleSetClipSelectionMode = useCallback(
    (nextClipSelectionMode: ClipSelectionMode) => {
      stateDispatch({
        type: 'clip-selection-mode-change',
        payload: { mode: nextClipSelectionMode },
      });
    },
    [stateDispatch],
  );

  const handleDurationChange = useCallback(
    (millis: number) => {
      stateDispatch({
        type: 'video-duration-change',
        payload: { durationMillis: millis },
      });
    },
    [stateDispatch],
  );

  const handleClipMillisChange = useCallback(
    (startMillis: number, endMillis: number) => {
      stateDispatch({
        type: 'clip-millis-change',
        payload: { startMillis, endMillis },
      });
    },
    [stateDispatch],
  );

  const handleClipContinue = useCallback(() => {
    stateDispatch({ type: 'clip-step-submit' });
    wizardRef.current?.next();
  }, [stateDispatch, wizardRef]);

  const step: Step<VideoFlowStep.CLIP_VIDEO> = {
    completedName: 'Video Clipped',
    component: (
      <ClipVideoStep
        aspectRatioName={aspectRatioName}
        audioPeaks={audioPeaks}
        captionsControl={
          <ClipVideoStepContinueButton
            {...{ canTranscribe, clipSelectionMode, outOfTimeMessage }}
            onPress={handleClipContinue}
          />
        }
        clipEndMillis={clipEndMillis}
        clipSelectionMode={clipSelectionMode}
        clipStartMillis={clipStartMillis}
        onChangeClipSelectionMode={handleSetClipSelectionMode}
        onDurationChange={handleDurationChange}
        onSelectedMillisChange={handleClipMillisChange}
        showTranscriptionBalance={showBalance}
        showWaveformLoader={isUploadingEntireVideo}
        src={videoSrcUrl}
        transcriptionBalanceMillis={newBalanceMillis}
      />
    ),
    name: 'Clip Video',
    renderNextButton: ({ className, ...props }) =>
      clipSelectionMode === 'clip' ? null : (
        <ClipVideoStepContinueButton
          {...{ ...props, canTranscribe, clipSelectionMode, outOfTimeMessage }}
          onPress={handleClipContinue}
        />
      ),
    stepId: VideoFlowStep.CLIP_VIDEO,
  };

  return {
    // TODO - maybe body modifiers instead
    bodyClassName: block('body', {
      [clipSelectionMode]: !!clipSelectionMode,
    }),
    buttonClassName: block('buttons', {
      [clipSelectionMode]: !!clipSelectionMode,
      'with-balance': showBalance,
    }),
    step,
  };
}
